import { ButtonGroup } from '@chakra-ui/react';
import React, { ComponentType, FC } from 'react';
import {
  ItemTypeToComponent,
  PaginationComponentProps,
  UltimatePaginationProps,
  createUltimatePagination,
} from 'react-ultimate-pagination';
import { i18n } from '../../../i18n/translate';
import { formatClassName } from '../../../utils';
import { Button, ButtonSize, ButtonVariant } from '../Button';
import { IconButton } from '../IconButton';
import { PaginationComponentButtonProps, PaginationComponentWrapperProps } from './types';

const PaginationComponentButton: FC<PaginationComponentButtonProps> = props => {
  const { isDisabled, label, onClick, value } = props;

  return (
    <Button
      // variant="outline"
      isActive={props.isActive}
      isDisabled={isDisabled}
      onClick={onClick}
      aria-label={label}
    >
      {value}
    </Button>
  );
};

const PaginationComponentPage: FC<PaginationComponentProps> = props =>
  PaginationComponentButton(props as PaginationComponentButtonProps);

const PaginationComponentEllipsis: FC<PaginationComponentProps> = props =>
  PaginationComponentButton({
    ...props,
    isDisabled: true,
    onClick: () => {},
    value: '…',
    label: i18n.t('More'),
  } as PaginationComponentButtonProps);

const PaginationComponentFirstPageLink: FC<PaginationComponentProps> = props =>
  PaginationComponentButton({
    ...props,
    isDisabled: props.isActive,
    isArrowButton: true,
    value: '«',
    label: i18n.t('First'),
  } as PaginationComponentButtonProps);

const PaginationComponentPreviousPageLink: FC<PaginationComponentProps> = props => {
  return (
    <IconButton
      ariaLabel={i18n.t('Previous')}
      icon={{ prefix: 'fas', iconName: 'arrow-left' }}
      variant="ghost"
      size="compact"
      isDisabled={props.isActive}
      onClick={props.onClick}
    />
  );
};

const PaginationComponentNextPageLink: FC<PaginationComponentProps> = props => {
  return (
    <IconButton
      ariaLabel={i18n.t('Next')}
      icon={{ prefix: 'fas', iconName: 'arrow-right' }}
      variant="ghost"
      size="compact"
      isDisabled={props.isActive}
      onClick={props.onClick}
    />
  );
};

const PaginationComponentLastPageLink: FC<PaginationComponentProps> = props =>
  PaginationComponentButton({
    ...props,
    isDisabled: props.isActive,
    isArrowButton: true,
    value: '»',
    label: i18n.t('Last'),
  } as PaginationComponentButtonProps);

const PaginationComponentWrapper: FC<PaginationComponentWrapperProps> = props => {
  // set class name based on pagination component usage
  const className = formatClassName([
    props.coreTable ? 'core-pagination' : 'pagination',
    { 'pagination-branded': props.isBranded },
  ]);

  return (
    <ButtonGroup
      className={className}
      variant={'outline'}
      size={'compact'}
      colorScheme={props.isBranded ? 'brand' : 'gray'}
    >
      {props.children}
    </ButtonGroup>
  );
};

const itemTypeToComponent: ItemTypeToComponent = {
  PAGE: PaginationComponentPage,
  ELLIPSIS: PaginationComponentEllipsis,
  FIRST_PAGE_LINK: PaginationComponentFirstPageLink,
  PREVIOUS_PAGE_LINK: PaginationComponentPreviousPageLink,
  NEXT_PAGE_LINK: PaginationComponentNextPageLink,
  LAST_PAGE_LINK: PaginationComponentLastPageLink,
};

const UltimatePagination = createUltimatePagination({
  itemTypeToComponent,
  WrapperComponent: PaginationComponentWrapper,
});

export const PaginationComponent = UltimatePagination as ComponentType<
  UltimatePaginationProps & PaginationComponentWrapperProps
>;
